import { ComponentAction, FindNativeFeature, } from '@standardnotes/features';
import { UIFeature } from '@standardnotes/models';
import { AllowedBatchContentTypes, AllowedBatchStreaming } from '../Types';
import { Copy, filterFromArray, removeFromArray, uniqueArray } from '@standardnotes/utils';
import { permissionsStringForPermissions } from '../permissionsStringForPermissions';
import { c } from 'ttag';
export class RunWithPermissionsUseCase {
    constructor(permissionDialogUIHandler, alerts, mutator, sync, items) {
        this.permissionDialogUIHandler = permissionDialogUIHandler;
        this.alerts = alerts;
        this.mutator = mutator;
        this.sync = sync;
        this.items = items;
        this.permissionDialogs = [];
        this.pendingErrorAlerts = new Set();
    }
    deinit() {
        this.permissionDialogs = [];
        this.permissionDialogUIHandler = undefined;
        this.alerts = undefined;
        this.mutator = undefined;
        this.sync = undefined;
        this.items = undefined;
    }
    execute(componentIdentifier, requiredPermissions, runFunction) {
        const uiFeature = this.findUIFeature(componentIdentifier);
        if (!uiFeature) {
            if (!this.pendingErrorAlerts.has(componentIdentifier)) {
                this.pendingErrorAlerts.add(componentIdentifier);
                void this.alerts
                    .alert(c('B4.Notes.EditingUI.Error')
                    .t `Unable to find component with ID ${componentIdentifier}. Please restart the app and try again.`, c('B4.Notes.EditingUI.Error').t `An unexpected error occurred`)
                    .then(() => {
                    this.pendingErrorAlerts.delete(componentIdentifier);
                });
            }
            return;
        }
        if (uiFeature.isNativeFeature) {
            runFunction();
            return;
        }
        if (!this.areRequestedPermissionsValid(uiFeature, requiredPermissions)) {
            console.error('Component is requesting invalid permissions', componentIdentifier, requiredPermissions);
            return;
        }
        const acquiredPermissions = uiFeature.acquiredPermissions;
        /* Make copy as not to mutate input values */
        requiredPermissions = Copy(requiredPermissions);
        for (const required of requiredPermissions.slice()) {
            /* Remove anything we already have */
            const respectiveAcquired = acquiredPermissions.find((candidate) => candidate.name === required.name);
            if (!respectiveAcquired) {
                continue;
            }
            /* We now match on name, lets substract from required.content_types anything we have in acquired. */
            const requiredContentTypes = required.content_types;
            if (!requiredContentTypes) {
                /* If this permission does not require any content types (i.e stream-context-item)
                  then we can remove this from required since we match by name (respectiveAcquired.name === required.name) */
                filterFromArray(requiredPermissions, required);
                continue;
            }
            for (const acquiredContentType of respectiveAcquired.content_types) {
                removeFromArray(requiredContentTypes, acquiredContentType);
            }
            if (requiredContentTypes.length === 0) {
                /* We've removed all acquired and end up with zero, means we already have all these permissions */
                filterFromArray(requiredPermissions, required);
            }
        }
        if (requiredPermissions.length > 0) {
            this.promptForPermissionsWithDeferredRendering(uiFeature.asComponent, requiredPermissions, 
            // eslint-disable-next-line @typescript-eslint/require-await
            async (approved) => {
                if (approved) {
                    runFunction();
                }
            });
        }
        else {
            runFunction();
        }
    }
    setPermissionDialogUIHandler(handler) {
        this.permissionDialogUIHandler = handler;
    }
    areRequestedPermissionsValid(uiFeature, permissions) {
        var _a;
        for (const permission of permissions) {
            if (permission.name === ComponentAction.StreamItems) {
                if (!AllowedBatchStreaming.includes(uiFeature.featureIdentifier)) {
                    return false;
                }
                const hasNonAllowedBatchPermission = (_a = permission.content_types) === null || _a === void 0 ? void 0 : _a.some((type) => !AllowedBatchContentTypes.includes(type));
                if (hasNonAllowedBatchPermission) {
                    return false;
                }
            }
        }
        return true;
    }
    promptForPermissionsWithDeferredRendering(component, permissions, callback) {
        setTimeout(() => {
            this.promptForPermissions(component, permissions, callback);
        });
    }
    promptForPermissions(component, permissions, callback) {
        const params = {
            component: component,
            permissions: permissions,
            permissionsString: permissionsStringForPermissions(permissions, component),
            actionBlock: callback,
            callback: async (approved) => {
                const latestComponent = this.items.findItem(component.uuid);
                if (!latestComponent) {
                    return;
                }
                if (approved) {
                    const componentPermissions = Copy(latestComponent.permissions);
                    for (const permission of permissions) {
                        const matchingPermission = componentPermissions.find((candidate) => candidate.name === permission.name);
                        if (!matchingPermission) {
                            componentPermissions.push(permission);
                        }
                        else {
                            /* Permission already exists, but content_types may have been expanded */
                            const contentTypes = matchingPermission.content_types || [];
                            matchingPermission.content_types = uniqueArray(contentTypes.concat(permission.content_types));
                        }
                    }
                    await this.mutator.changeItem(component, (m) => {
                        const mutator = m;
                        mutator.permissions = componentPermissions;
                    });
                    void this.sync.sync();
                }
                this.permissionDialogs = this.permissionDialogs.filter((pendingDialog) => {
                    var _a, _b;
                    /* Remove self */
                    if (pendingDialog === params) {
                        (_a = pendingDialog.actionBlock) === null || _a === void 0 ? void 0 : _a.call(pendingDialog, approved);
                        return false;
                    }
                    const containsObjectSubset = (source, target) => {
                        return !target.some((val) => !source.find((candidate) => JSON.stringify(candidate) === JSON.stringify(val)));
                    };
                    if (pendingDialog.component === component) {
                        /* remove pending dialogs that are encapsulated by already approved permissions, and run its function */
                        if (pendingDialog.permissions === permissions ||
                            containsObjectSubset(permissions, pendingDialog.permissions)) {
                            /* If approved, run the action block. Otherwise, if canceled, cancel any
                            pending ones as well, since the user was explicit in their intentions */
                            if (approved) {
                                (_b = pendingDialog.actionBlock) === null || _b === void 0 ? void 0 : _b.call(pendingDialog, approved);
                            }
                            return false;
                        }
                    }
                    return true;
                });
                if (this.permissionDialogs.length > 0) {
                    this.permissionDialogUIHandler(this.permissionDialogs[0]);
                }
            },
        };
        /**
         * Since these calls are asyncronous, multiple dialogs may be requested at the same time.
         * We only want to present one and trigger all callbacks based on one modal result
         */
        const existingDialog = this.permissionDialogs.find((dialog) => dialog.component === component);
        this.permissionDialogs.push(params);
        if (!existingDialog) {
            this.permissionDialogUIHandler(params);
        }
    }
    findUIFeature(identifier) {
        const nativeFeature = FindNativeFeature(identifier);
        if (nativeFeature) {
            return new UIFeature(nativeFeature);
        }
        const componentItem = this.items.findItem(identifier);
        if (componentItem) {
            return new UIFeature(componentItem);
        }
        return undefined;
    }
}
